import { maximizeYieldAlgorithm } from './algorithms/maximizeYield';
import { companionPlantingAlgorithm } from './algorithms/companionPlanting';
import { plantRotationAlgorithm } from './algorithms/plantRotation';
import { successionPlantingAlgorithm } from './algorithms/successionPlanting';
import { pollinatorFriendlyAlgorithm } from './algorithms/pollinatorFriendly';
import { waterEfficiencyAlgorithm } from './algorithms/waterEfficiency';
import { organicPestControlAlgorithm } from './algorithms/organicPestControl';
import {validatePlacement, createPlantMap, placePlant, fillRemainingOpenAreas, fetchCompanionPlants,
    fetchOrganicPestControlPlants, fetchPollinatorPlants} from './utils/gardenUtils';

// Algorithm registry - maps algorithm names to their implementations
const ALGORITHMS = {
    'maximizeYield': maximizeYieldAlgorithm,
    'companionPlanting': companionPlantingAlgorithm,
    'plantRotation': plantRotationAlgorithm,
    'successionPlanting': successionPlantingAlgorithm,
    'pollinatorFriendly': pollinatorFriendlyAlgorithm,
    'waterEfficiency': waterEfficiencyAlgorithm,
    'organicPestControl': organicPestControlAlgorithm,
};

/**
 * Main function to execute planting algorithms
 * @param {Object} params - Algorithm parameters
 * @param {string} params.algorithmType - Type of algorithm to run
 * @param {Array} params.gardenMatrix - 2D array representing garden layout
 * @param {Array} params.plants - Available plants with counts
 * @param {Object} params.config - Algorithm configuration options
 * @param {Object} params.personalizedOptions - Personalized planting options
 * @param {Object} params.climateData - Climate and zone information
 * @param {Array} params.previousSeasonData - Data from previous plantings
 * @returns {Object} Result containing new plant map and metadata
 */
export const runPlantingAlgorithm = async (params) => {
    const {
        algorithmType,
        gardenMatrix,
        plants,
        config = {},
        personalizedOptions = {},
        climateData = {},
        previousSeasonData = []
    } = params;

    console.log(plants);

    // Validate inputs
    if (!algorithmType || !ALGORITHMS[algorithmType]) {
        throw new Error(`Unknown algorithm type: ${algorithmType}`);
    }

    if (!gardenMatrix || !plants) {
        throw new Error('Garden matrix and plants are required');
    }

    // Prepare algorithm context
    const algorithmContext = {
        gardenMatrix,
        currentPlantMap: params.currentPlantMap,
        plants: [...plants], // Create copy to avoid mutation
        config,
        personalizedOptions,
        climateData,
        previousSeasonData,
        utils: {
            validatePlacement,
            createPlantMap,
            placePlant,
            fillRemainingOpenAreas,
            fetchCompanionPlants,
            fetchOrganicPestControlPlants,
            fetchPollinatorPlants,
        }
    };

    try {
        // Execute the selected algorithm
        const algorithm = ALGORITHMS[algorithmType];
        const result = await algorithm(algorithmContext);

        // Validate the result
        if (!result || !result.plantMap) {
            throw new Error('Algorithm failed to produce valid result');
        }

        return {
            success: true,
            plantMap: result.plantMap,
            metadata: {
                algorithmUsed: algorithmType,
                plantsPlaced: result.plantsPlaced || 0,
                unusedPlants: result.unusedPlants || [],
                warnings: result.warnings || [],
                efficiency: result.efficiency || 0,
                timestamp: new Date().toISOString()
            }
        };

    } catch (error) {
        return {
            success: false,
            error: error.message,
            plantMap: null,
            metadata: {
                algorithmUsed: algorithmType,
                timestamp: new Date().toISOString()
            }
        };
    }
};

// Utility function to get available algorithms
export const getAvailableAlgorithms = () => {
    return Object.keys(ALGORITHMS);
};

// Function to validate algorithm configuration
export const validateAlgorithmConfig = (algorithmType, config) => {
    const algorithm = ALGORITHMS[algorithmType];
    if (!algorithm) {
        return { valid: false, errors: [`Unknown algorithm: ${algorithmType}`] };
    }

    // Each algorithm can implement its own validation
    if (algorithm.validateConfig) {
        return algorithm.validateConfig(config);
    }

    return { valid: true, errors: [] };
};

// Preset management functions
export const savePreset = (presetName, config) => {
    // Handle preset saving logic
    const presets = getStoredPresets();
    presets[presetName] = {
        config,
        createdAt: new Date().toISOString()
    };
    // Save to storage (you'd implement this)
    localStorage.setItem('gardenPresets', JSON.stringify(presets));
};

export const getStoredPresets = () => {
    try {
        return JSON.parse(localStorage.getItem('gardenPresets') || '{}');
    } catch {
        return {};
    }
};

export const deletePreset = (presetName) => {
    const presets = getStoredPresets();
    delete presets[presetName];
    localStorage.setItem('gardenPresets', JSON.stringify(presets));
};